import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { getCoursesPath } from '../utils/navigationHelper';
import LessonTextRenderer from '../components/LessonTextRenderer';
import {
  DocumentIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  LinkIcon,
  ChatBubbleLeftIcon,
  MapPinIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
  AcademicCapIcon,
  PhotoIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const CourseViewPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const audioRef = useRef(null);
  const timeAlreadySentRef = useRef(false); // Track if time was already sent to prevent duplicates
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [quizAttempts, setQuizAttempts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [lessonStartTime, setLessonStartTime] = useState(null); // Track when lesson was opened
  const [linkedPastQuestions, setLinkedPastQuestions] = useState({});
  const [linkedPastQuestionsLoading, setLinkedPastQuestionsLoading] = useState({});
  const [linkedPastQuestionsError, setLinkedPastQuestionsError] = useState({});
  const [transcriptionModal, setTranscriptionModal] = useState({
    isOpen: false,
    loading: false,
    text: '',
    words: [],
    error: '',
    audioUrl: ''
  });

  useEffect(() => {
    fetchCourseContent();
  }, [courseId]);

  // Cleanup: send time spent when component unmounts or lesson changes
  useEffect(() => {
    return () => {
      // Only send time if we have a valid token (user is still logged in)
      // AND we haven't already sent the time via the close button
      const token = localStorage.getItem('token');
      if (expandedLesson && lessonStartTime && user?.role === 'student' && token && !timeAlreadySentRef.current) {
        const timeSpent = Math.floor((Date.now() - lessonStartTime) / 1000);
        // Only send if we actually spent meaningful time (> 0 seconds)
        if (timeSpent > 0) {
          // Use navigator.sendBeacon for reliable tracking on page unload
          const data = JSON.stringify({ timeSpent });
          const blob = new Blob([data], { type: 'application/json' });
          
          // Try sendBeacon first (works on page unload)
          try {
            const url = `${process.env.REACT_APP_API_URL || 'https://debestcollege-6e2a2a9de346.herokuapp.com/api'}/lessons/${expandedLesson}/mark-viewed`;
            navigator.sendBeacon(url, blob);
          } catch (err) {
            // Fallback: try regular API call (only if token exists)
            api.post(`/lessons/${expandedLesson}/mark-viewed`, { timeSpent }).catch(() => {
              // Silently fail - time was already saved when lesson was closed
            });
          }
        }
      }
      // Reset the flag when cleanup completes
      timeAlreadySentRef.current = false;
    };
  }, [expandedLesson, lessonStartTime, user]);

  // Initialize audio element when transcription is loaded
  useEffect(() => {
    if (audioRef.current && transcriptionModal.audioUrl && !transcriptionModal.loading) {
      // Reset audio state
      audioRef.current.currentTime = 0;
      audioRef.current.volume = volume;
      setCurrentTime(0);
      setCurrentWordIndex(-1);
      setIsPlaying(false);
      
      // Load the audio to get duration
      audioRef.current.load();
    }
  }, [transcriptionModal.audioUrl, transcriptionModal.loading]);

  // Handle ESC key to close transcription modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && transcriptionModal.isOpen) {
        closeTranscriptionModal();
      }
    };

    if (transcriptionModal.isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [transcriptionModal.isOpen]);

  const fetchCourseContent = async () => {
    try {
      setLoading(true);
      // Fetch course details
      const courseRes = await api.get(`/courses/${courseId}`);
      setCourse(courseRes.data);

      // Fetch lessons filtered by student type
      const lessonsRes = await api.get(
        `/lessons/course/${courseId}?studentType=${user?.studentType || ''}`
      );
      console.log('Fetched lessons:', lessonsRes.data);
      setLessons(lessonsRes.data);

      // Fetch student's quiz attempts
      try {
        const attemptsRes = await api.get(`/submissions`);
        const attemptsMap = {};
        const submissions = attemptsRes.data.data || attemptsRes.data;
        submissions.forEach(submission => {
          if (!attemptsMap[submission.quizId]) {
            attemptsMap[submission.quizId] = 0;
          }
          attemptsMap[submission.quizId]++;
        });
        console.log('Quiz attempts map:', attemptsMap);
        setQuizAttempts(attemptsMap);
      } catch (err) {
        console.error('Error fetching attempts:', err);
      }
    } catch (err) {
      console.error('Error fetching course content:', err);
      setError(err.response?.data?.error || 'Failed to load course content');
    } finally {
      setLoading(false);
    }
  };

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'image':
        return <PhotoIcon className="h-6 w-6 text-indigo-600" />;
      case 'video':
        return <VideoCameraIcon className="h-6 w-6 text-purple-600" />;
      case 'audio':
        return <MusicalNoteIcon className="h-6 w-6 text-pink-600" />;
      case 'document':
        return <DocumentIcon className="h-6 w-6 text-blue-600" />;
      case 'link':
        return <LinkIcon className="h-6 w-6 text-green-600" />;
      case 'text':
      default:
        return <ChatBubbleLeftIcon className="h-6 w-6 text-gray-600" />;
    }
  };

  const toggleLesson = (lesson) => {
    const lessonId = lesson.id;
    const wasCollapsed = expandedLesson !== lessonId;
    
    // If closing a lesson, send time spent
    if (!wasCollapsed && expandedLesson === lessonId && lessonStartTime && user?.role === 'student') {
      const timeSpent = Math.floor((Date.now() - lessonStartTime) / 1000); // in seconds
      console.log('⏱️ Closing lesson. Time spent:', timeSpent, 'seconds');
      // Mark that we're about to send the time (prevent cleanup from also sending)
      timeAlreadySentRef.current = true;
      // Reset timer BEFORE calling the API to prevent cleanup effect from also sending
      setLessonStartTime(null);
      markLessonAsViewed(lessonId, timeSpent);
    }
    
    setExpandedLesson(expandedLesson === lessonId ? null : lessonId);
    
    // If opening (expanding) the lesson, mark it as viewed and start timer
    if (wasCollapsed && user?.role === 'student') {
      console.log('▶️ Opening lesson. Starting timer...');
      timeAlreadySentRef.current = false; // Reset flag when opening new lesson
      setLessonStartTime(Date.now());
      markLessonAsViewed(lessonId, 0); // Initial mark with 0 time
    }

    if (wasCollapsed && lesson.addPastQuestions) {
      fetchLessonLinkedPastQuestions(lesson);
    }
  };

  const markLessonAsViewed = async (lessonId, timeSpent = 0) => {
    try {
      console.log('📤 Sending time to API:', timeSpent, 'seconds for lesson', lessonId);
      const response = await api.post(`/lessons/${lessonId}/mark-viewed`, { timeSpent });
      console.log('✅ API response:', response.data);
      // Update the lesson in state to reflect it's been viewed
      setLessons(prevLessons => 
        prevLessons.map(lesson => 
          lesson.id === lessonId 
            ? { ...lesson, viewedAt: new Date().toISOString() }
            : lesson
        )
      );
    } catch (err) {
      console.error('❌ Error marking lesson as viewed:', err);
      // Non-critical error, don't show to user
    }
  };

  const handleTakeQuiz = (quizId) => {
    navigate(`/students/dashboard/quizzes/${quizId}/attempt`);
  };

  const getPastQuestionReadPath = (questionId) => {
    if (user?.role === 'admin') return `/admin/dashboard/past-questions/read/${questionId}`;
    if (user?.role === 'teacher') return `/teachers/dashboard/past-questions/read/${questionId}`;
    return `/students/dashboard/past-questions/read/${questionId}`;
  };

  const getBotPath = (questionId) => {
    if (user?.role === 'admin') return `/admin/dashboard/bot/${questionId}`;
    if (user?.role === 'teacher') return `/teachers/dashboard/bot/${questionId}`;
    return `/students/dashboard/bot/${questionId}`;
  };

  const fetchLessonLinkedPastQuestions = async (lesson) => {
    if (!lesson?.id || !lesson?.addPastQuestions) return;
    if (linkedPastQuestions[lesson.id] || linkedPastQuestionsLoading[lesson.id]) return;

    setLinkedPastQuestionsLoading(prev => ({ ...prev, [lesson.id]: true }));
    setLinkedPastQuestionsError(prev => ({ ...prev, [lesson.id]: '' }));

    try {
      const res = await api.get(`/lessons/${lesson.id}/past-questions`);
      setLinkedPastQuestions(prev => ({ ...prev, [lesson.id]: res.data || null }));
    } catch (err) {
      setLinkedPastQuestionsError(prev => ({
        ...prev,
        [lesson.id]: err.response?.data?.error || 'Failed to load related past questions'
      }));
    } finally {
      setLinkedPastQuestionsLoading(prev => ({ ...prev, [lesson.id]: false }));
    }
  };

  const handleTranscribeAudio = async (audioUrl) => {
    // Pause all native audio elements in the lesson content
    const allAudioElements = document.querySelectorAll('audio');
    allAudioElements.forEach(audio => {
      if (audio !== audioRef.current) {
        audio.pause();
      }
    });

    setTranscriptionModal({
      isOpen: true,
      loading: true,
      text: '',
      words: [],
      error: '',
      audioUrl
    });

    try {
      const response = await api.post('/lessons/transcribe', { audioUrl });
      setTranscriptionModal(prev => ({
        ...prev,
        loading: false,
        text: response.data.transcription,
        words: response.data.words || []
      }));
    } catch (err) {
      console.error('Transcription error:', err);
      setTranscriptionModal(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.error || 'Failed to transcribe audio. Please try again.'
      }));
    }
  };

  // Handle audio time update for synchronized highlighting
  const handleAudioTimeUpdate = () => {
    if (!audioRef.current || !transcriptionModal.words.length) return;
    
    const currentTime = audioRef.current.currentTime;
    setCurrentTime(currentTime);
    
    // Find the current word based on audio playback time
    // Use the word where currentTime falls between start and end
    const wordIndex = transcriptionModal.words.findIndex((word, idx) => {
      const wordStart = word.start;
      const wordEnd = word.end || word.start + 0.5; // Fallback if no end time
      const nextWord = transcriptionModal.words[idx + 1];
      
      // Check if current time is within this word's timeframe
      if (currentTime >= wordStart && currentTime < wordEnd) {
        return true;
      }
      
      // Also handle the gap between words - show the previous word
      if (nextWord && currentTime >= wordEnd && currentTime < nextWord.start) {
        return true;
      }
      
      return false;
    });
    
    if (wordIndex !== currentWordIndex) {
      setCurrentWordIndex(wordIndex);
      
      // Auto-scroll to the highlighted word
      if (wordIndex >= 0) {
        const wordElement = document.querySelector(`[data-word-index="${wordIndex}"]`);
        if (wordElement) {
          wordElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  // Audio player controls
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      // For iOS, we need to ensure audio is loaded before playing
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Audio started playing successfully
            console.log('Audio playback started');
          })
          .catch(err => {
            console.error('Error playing audio:', err);
            // Show user-friendly error
            if (err.name === 'NotAllowedError') {
              alert('Please interact with the page first to enable audio playback.');
            } else if (err.name === 'NotSupportedError') {
              alert('This audio format is not supported on your device.');
            } else {
              alert('Unable to play audio. Please try again.');
            }
          });
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const closeTranscriptionModal = () => {
    // Stop and reset audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentWordIndex(-1);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setVolume(1);
    setTranscriptionModal({
      isOpen: false,
      loading: false,
      text: '',
      words: [],
      error: '',
      audioUrl: ''
    });
  };

  // Helper: filter lessons by search query
  const filterLessonsBySearch = (lessonsArray) => {
    if (!searchQuery) return lessonsArray;
    const query = searchQuery.toLowerCase();
    return lessonsArray.filter(lesson => 
      lesson.title.toLowerCase().includes(query) ||
      (lesson.description && lesson.description.toLowerCase().includes(query))
    );
  };

  // Helper: get lesson status badge (NEW or UPDATED)
  const getLessonBadge = (lesson) => {
    // Don't show badge if student has already viewed this lesson
    if (lesson.viewedAt) {
      return null;
    }

    const now = new Date();
    const threeDaysAgo = new Date(now.getTime() - (3 * 24 * 60 * 60 * 1000)); // 3 days
    const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000)); // 7 days
    
    const publishedDate = new Date(lesson.publishedAt);
    const updatedDate = new Date(lesson.updatedAt);
    const createdDate = new Date(lesson.createdAt);
    
    // Check if lesson is new (published in last 7 days)
    if (lesson.publishedAt && publishedDate > sevenDaysAgo) {
      return { type: 'NEW', color: 'green' };
    }
    
    // Check if lesson is recently updated (updated in last 3 days)
    // Only show UPDATED if:
    // 1. Published more than 7 days ago (no longer "new")
    // 2. Updated in last 3 days
    // 3. Updated significantly after creation (more than 1 day difference)
    if (lesson.updatedAt && 
        lesson.publishedAt &&
        publishedDate <= sevenDaysAgo && // No longer "new"
        updatedDate > threeDaysAgo && 
        (updatedDate - createdDate) > (24 * 60 * 60 * 1000)) { // More than 1 day after creation
      return { type: 'UPDATED', color: 'blue' };
    }
    
    return null;
  };

  const getQuizStatus = (quiz) => {
    const now = new Date();
    const openTime = quiz.openTime ? new Date(quiz.openTime) : null;
    const closeTime = quiz.closeTime ? new Date(quiz.closeTime) : null;
    const attemptCount = quizAttempts[quiz.id] || 0;
    const maxAttempts = quiz.maxAttempts || null;

    // Check if max attempts reached
    if (maxAttempts && attemptCount >= maxAttempts) {
      return { status: 'exhausted', label: 'No Attempts Left', color: 'orange' };
    }

    // Check time-based availability
    if (openTime && now < openTime) {
      return { status: 'upcoming', label: 'Not Yet Available', color: 'gray' };
    }
    if (closeTime && now > closeTime) {
      return { status: 'closed', label: 'Closed', color: 'red' };
    }
    
    return { 
      status: 'open', 
      label: maxAttempts ? `${maxAttempts - attemptCount} Attempt${maxAttempts - attemptCount !== 1 ? 's' : ''} Left` : 'Available', 
      color: 'green' 
    };
  };

  const renderContent = (lesson) => {
    console.log('Rendering lesson:', lesson);
    console.log('Content blocks:', lesson.contentBlocks);
    console.log('Content type:', lesson.contentType);
    
    // Check if using new multiple content blocks format
    if (lesson.contentBlocks && Array.isArray(lesson.contentBlocks)) {
      console.log('Using content blocks format');
      return (
        <div className="space-y-6">
          {lesson.contentBlocks.map((block, index) => (
            <div key={index} className="content-block">
              {block.type === 'text' && (
                <div className="bg-white rounded-lg p-2 sm:p-6 shadow-sm border border-gray-100 w-full">
                  <LessonTextRenderer content={block.content} />
                </div>
              )}

              {block.type === 'image' && block.contentUrl && (
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={block.contentUrl}
                    alt={`Content ${index + 1}`}
                    className="w-full h-auto max-h-[600px] object-contain bg-gray-100"
                  />
                </div>
              )}

              {block.type === 'video' && block.contentUrl && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    controls
                    controlsList="nodownload"
                    className="w-full h-full"
                    src={block.contentUrl}
                    preload="metadata"
                    playsInline
                    crossOrigin="anonymous"
                  >
                    <track kind="captions" label="English" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              {block.type === 'audio' && block.contentUrl && (
                <div className="bg-gray-100 rounded-lg p-6">
                  <audio
                    controls
                    controlsList="nodownload"
                    className="w-full"
                    src={block.contentUrl}
                    preload="metadata"
                    crossOrigin="anonymous"
                  >
                    <track kind="captions" label="English" />
                    Your browser does not support the audio tag.
                  </audio>
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={() => handleTranscribeAudio(block.contentUrl)}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Transcribe Audio
                    </button>
                  </div>
                </div>
              )}

              {block.type === 'document' && block.contentUrl && (
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <DocumentIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-700 mb-4">Document attached</p>
                  <a
                    href={block.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                  >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    Open Document
                  </a>
                </div>
              )}

              {block.type === 'link' && block.content && (
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <LinkIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
                  <p className="text-gray-700 mb-4">External resource</p>
                  <a
                    href={block.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                  >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    Open Link
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Fallback to old single content format
    switch (lesson.contentType) {
      case 'text':
        return (
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-100">
            <LessonTextRenderer content={lesson.textContent} />
          </div>
        );

      case 'image':
        return (
          <div className="rounded-lg overflow-hidden">
            <img
              src={lesson.contentUrl}
              alt={lesson.title}
              className="w-full h-auto max-h-[600px] object-contain bg-gray-100"
            />
          </div>
        );

      case 'video':
        return (
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            <video
              controls
              className="w-full h-full"
              src={lesson.contentUrl}
              preload="metadata"
              playsInline
              crossOrigin="anonymous"
            >
              <track kind="captions" label="English" />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="bg-gray-100 rounded-lg p-6">
            <audio
              controls
              controlsList="nodownload"
              className="w-full"
              src={lesson.contentUrl}
              preload="metadata"
              crossOrigin="anonymous"
            >
              <track kind="captions" label="English" />
              Your browser does not support the audio tag.
            </audio>
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => handleTranscribeAudio(lesson.contentUrl)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Transcribe Audio
              </button>
            </div>
          </div>
        );

      case 'document':
        return (
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <DocumentIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
            <p className="text-gray-700 mb-4">Document attached to this lesson</p>
            <a
              href={lesson.contentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              Open Document
            </a>
          </div>
        );

      case 'link':
        return (
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <LinkIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <p className="text-gray-700 mb-4">External resource</p>
            <a
              href={lesson.contentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
            >
              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
              Visit Link
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading course content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* On very small screens allow full width; keep centered max width on larger screens */}
    {/* Use the full viewport width on the smallest screens and remove extra side padding so
      lesson content can span nearly edge-to-edge; keep a centered max width on larger screens */}
  <div className="w-screen max-w-full sm:max-w-6xl mx-0 px-0 sm:px-2 md:px-6 lg:px-8 py-6">
        {/* Header with Back Button */}
        <button
          onClick={() => navigate(getCoursesPath(location, user))}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 group transition-all"
        >
          <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="font-medium">Back to My Courses</span>
        </button>

        {/* Course Header Card */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          {/* Decorative Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-24 -translate-x-24"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-white bg-opacity-20 rounded-xl p-3 backdrop-blur-sm">
                <AcademicCapIcon className="h-10 w-10" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl sm:text-4xl font-bold mb-2 leading-tight">{course?.title}</h1>
                {course?.description && (
                  <p className="text-blue-100 text-sm sm:text-lg leading-relaxed">{course.description}</p>
                )}
                {course?.teacher && (
                  <div className="flex items-center gap-2 mt-3 text-blue-100">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                    </svg>
                    <span className="font-medium">Instructor: {course.teacher.name}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-white border-opacity-20">
              <div className="flex items-center gap-2 bg-white bg-opacity-20 rounded-lg px-4 py-2 backdrop-blur-sm">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
                </svg>
                <span className="font-semibold">
                  {searchQuery ? (
                    <>
                      {filterLessonsBySearch(lessons).length} of {lessons.length} lessons
                    </>
                  ) : (
                    <>{lessons.length} lesson{lessons.length !== 1 ? 's' : ''} available</>
                  )}
                </span>
              </div>

              {lessons.some(l => !l.viewedAt) && (
                <div className="flex items-center gap-2 bg-green-500 bg-opacity-30 rounded-lg px-4 py-2 backdrop-blur-sm border border-green-400">
                  <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
                  </svg>
                  <span className="font-semibold">
                    {lessons.filter(l => !l.viewedAt).length} new lesson{lessons.filter(l => !l.viewedAt).length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-lg mb-6 shadow-md">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

      {/* Search Bar */}
      {lessons.length > 0 && (
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="🔍 Search lessons by title or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 text-lg border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            {searchQuery && (
              <div className="mt-3 flex items-center justify-between">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
                  </svg>
                  Showing <strong className="mx-1">{filterLessonsBySearch(lessons).length}</strong> of <strong className="mx-1">{lessons.length}</strong> lessons
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium underline flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lessons */}
      {lessons.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-300">
          <div className="inline-block p-6 bg-gray-100 rounded-full mb-4">
            <ChatBubbleLeftIcon className="h-16 w-16 text-gray-400" />
          </div>
          <p className="text-gray-700 text-xl font-semibold mb-2">No lessons available yet</p>
          <p className="text-gray-500 text-lg">Check back later for course content</p>
        </div>
      ) : filterLessonsBySearch(lessons).length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-300">
          <div className="inline-block p-6 bg-blue-100 rounded-full mb-4">
            <MagnifyingGlassIcon className="h-16 w-16 text-blue-400" />
          </div>
          <p className="text-gray-700 text-xl font-semibold mb-2">No lessons found</p>
          <p className="text-gray-500 text-lg mb-4">No lessons match your search &quot;{searchQuery}&quot;</p>
          <button
            onClick={() => setSearchQuery('')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
            Clear search
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {filterLessonsBySearch(lessons).map((lesson) => {
            const isExpanded = expandedLesson === lesson.id;
            const badge = getLessonBadge(lesson);
            return (
              <div
                key={lesson.id}
                className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-l-4 overflow-hidden transform hover:-translate-y-1 ${
                  lesson.isPinned ? 'border-yellow-400 ring-2 ring-yellow-200' : 'border-blue-500'
                } ${!lesson.viewedAt ? 'ring-2 ring-green-200' : ''}`}
              >
                {/* Lesson Header */}
                <div
                  className={`p-6 cursor-pointer transition-colors ${
                    isExpanded ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleLesson(lesson)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleLesson(lesson);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-4 flex-1 w-full">
                      {/* Icon with background */}
                      <div className={`p-3 rounded-xl ${
                        lesson.contentType === 'video' ? 'bg-purple-100' :
                        lesson.contentType === 'audio' ? 'bg-pink-100' :
                        lesson.contentType === 'document' ? 'bg-blue-100' :
                        lesson.contentType === 'image' ? 'bg-indigo-100' :
                        lesson.contentType === 'link' ? 'bg-green-100' :
                        'bg-gray-100'
                      }`}>
                        {getContentIcon(lesson.contentType)}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-xl font-bold text-gray-900">
                            {lesson.title}
                          </h3>
                          {lesson.isPinned && (
                            <div className="flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full border border-yellow-300">
                              <MapPinIcon className="h-4 w-4" />
                              <span className="text-xs font-bold">PINNED</span>
                            </div>
                          )}
                          {!lesson.viewedAt && (
                            <div className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full border border-green-300 animate-pulse">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                              </svg>
                              <span className="text-xs font-bold">NOT VIEWED</span>
                            </div>
                          )}
                          {badge && (
                            <span className={`px-3 py-1 rounded-full text-xs font-bold animate-pulse ${
                              badge.color === 'green' 
                                ? 'bg-green-500 text-white shadow-lg shadow-green-200' 
                                : 'bg-blue-500 text-white shadow-lg shadow-blue-200'
                            }`}>
                              ✨ {badge.type}
                            </span>
                          )}
                        </div>
                        {lesson.description && (
                          <p className="text-gray-600 mb-3 leading-relaxed">{lesson.description}</p>
                        )}
                        <div className="flex flex-wrap gap-3 text-sm">
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full font-medium">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd"/>
                            </svg>
                            {lesson.contentType.charAt(0).toUpperCase() + lesson.contentType.slice(1)}
                          </span>
                          {lesson.quizzes && lesson.quizzes.length > 0 && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                              </svg>
                              {lesson.quizzes.length} Quiz{lesson.quizzes.length !== 1 ? 'zes' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 md:mt-0 md:ml-4 flex-shrink-0">
                      <button
                        className={`p-3 rounded-xl transition-all ${
                          isExpanded 
                            ? 'bg-blue-600 text-white shadow-lg' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } transform ${isExpanded ? 'rotate-90' : ''}`}
                      >
                        <PlayIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t-2 border-gray-200 p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-blue-50">
                    {/* Lesson Content */}
                    <div className="mb-8">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-indigo-100 rounded-xl">
                          <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
                          </svg>
                        </div>
                        <h4 className="text-2xl font-bold text-gray-900">
                          📚 Lesson Content
                        </h4>
                      </div>
                      <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-100 w-full">
                        {renderContent(lesson)}
                      </div>
                    </div>

                    {/* Linked Past Questions */}
                    {lesson.addPastQuestions && (
                      <div className="mt-8 pt-8 border-t-2 border-gray-200">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-3 bg-emerald-100 rounded-xl">
                            <AcademicCapIcon className="h-6 w-6 text-emerald-600" />
                          </div>
                          <h4 className="text-2xl font-bold text-gray-900">
                            🎯 Related Past Questions
                          </h4>
                        </div>

                        {linkedPastQuestionsLoading[lesson.id] && (
                          <div className="bg-white rounded-2xl p-6 shadow border border-gray-200 text-gray-600">
                            Loading linked past questions...
                          </div>
                        )}

                        {!linkedPastQuestionsLoading[lesson.id] && linkedPastQuestionsError[lesson.id] && (
                          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700">
                            {linkedPastQuestionsError[lesson.id]}
                          </div>
                        )}

                        {!linkedPastQuestionsLoading[lesson.id] && !linkedPastQuestionsError[lesson.id] && linkedPastQuestions[lesson.id] && !linkedPastQuestions[lesson.id].enabled && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 text-yellow-800">
                            Past-question linkage is currently disabled for this lesson.
                          </div>
                        )}

                        {!linkedPastQuestionsLoading[lesson.id] && !linkedPastQuestionsError[lesson.id] && linkedPastQuestions[lesson.id]?.enabled && (!Array.isArray(linkedPastQuestions[lesson.id]?.similarQuestions) || linkedPastQuestions[lesson.id].similarQuestions.length === 0) && (
                          <div className="bg-white rounded-2xl p-6 shadow border border-gray-200 text-gray-600">
                            No related past questions found yet for this lesson.
                          </div>
                        )}

                        {!linkedPastQuestionsLoading[lesson.id] && !linkedPastQuestionsError[lesson.id] && linkedPastQuestions[lesson.id]?.enabled && Array.isArray(linkedPastQuestions[lesson.id]?.similarQuestions) && linkedPastQuestions[lesson.id].similarQuestions.length > 0 && (
                          <div className="space-y-4">
                            {linkedPastQuestions[lesson.id].similarQuestions.map((question, index) => (
                              <div
                                key={`${lesson.id}-${question.id}-${index}`}
                                className="bg-white rounded-2xl shadow border border-gray-200 p-4 sm:p-5"
                              >
                                <p className="text-gray-900 font-semibold mb-2 line-clamp-3">{question.text}</p>
                                <p className="text-xs sm:text-sm text-gray-500 mb-4">
                                  {question.subject || 'General'} {question.topic ? `• ${question.topic}` : ''} {question.year ? `• ${question.year}` : ''}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-2">
                                  <button
                                    onClick={() => navigate(getPastQuestionReadPath(question.id), { state: question })}
                                    className="w-full sm:w-auto px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
                                  >
                                    Open Question
                                  </button>
                                  <button
                                    onClick={() => navigate(getBotPath(question.id), {
                                      state: {
                                        question,
                                        meta: {
                                          courseId: lesson.courseId,
                                          topic: question.topic || lesson.topicHint || lesson.title,
                                          subject: question.subject || course?.title
                                        }
                                      }
                                    })}
                                    className="w-full sm:w-auto px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
                                  >
                                    Ask Bot
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Attached Quizzes */}
                    {lesson.quizzes && lesson.quizzes.length > 0 && (
                      <div className="mt-8 pt-8 border-t-2 border-gray-200">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-3 bg-blue-100 rounded-xl">
                            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                            </svg>
                          </div>
                          <h4 className="text-2xl font-bold text-gray-900">
                            📝 Assessments
                          </h4>
                        </div>
                        <div className="space-y-4">
                          {lesson.quizzes.map((quiz) => {
                            const quizStatus = getQuizStatus(quiz);
                            const isClosed = quizStatus.status === 'closed';
                            const isUpcoming = quizStatus.status === 'upcoming';
                            const isExhausted = quizStatus.status === 'exhausted';
                            const isDisabled = isClosed || isUpcoming || isExhausted;
                            
                            return (
                              <div
                                key={quiz.id}
                                className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-l-4 ${
                                  quizStatus.status === 'open' ? 'border-green-500' :
                                  quizStatus.status === 'closed' ? 'border-red-500' :
                                  quizStatus.status === 'exhausted' ? 'border-orange-500' :
                                  'border-gray-400'
                                }`}
                              >
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 md:p-6 gap-4 w-full">
                                  <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-3 mb-3">
                                      <h5 className="text-xl font-bold text-gray-900">
                                        {quiz.title}
                                      </h5>
                                      {quiz.lessonQuizMeta?.isRequired && (
                                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-bold border border-red-300">
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                                          </svg>
                                          REQUIRED
                                        </span>
                                      )}
                                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold shadow-md ${
                                        quizStatus.status === 'open' ? 'bg-green-500 text-white' :
                                        quizStatus.status === 'closed' ? 'bg-red-500 text-white' :
                                        quizStatus.status === 'exhausted' ? 'bg-orange-500 text-white' :
                                        'bg-gray-500 text-white'
                                      }`}>
                                        {quizStatus.status === 'open' && '✓'}
                                        {quizStatus.status === 'closed' && '✕'}
                                        {quizStatus.status === 'exhausted' && '⚠'}
                                        {quizStatus.status === 'upcoming' && '🕐'}
                                        {quizStatus.label}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                                      <span className="inline-flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 rounded-lg font-medium">
                                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                                          <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                        </svg>
                                        {quiz.questions?.length || 0} Question{quiz.questions?.length !== 1 ? 's' : ''}
                                      </span>
                                      {quiz.timeLimit && (
                                        <span className="inline-flex items-center gap-1.5 bg-purple-50 px-3 py-1.5 rounded-lg font-medium">
                                          <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                                          </svg>
                                          {quiz.timeLimit} Minutes
                                        </span>
                                      )}
                                    </div>
                                    {(quiz.openTime || quiz.closeTime) && (
                                      <div className="space-y-1 text-sm">
                                        {quiz.openTime && (
                                          <p className="text-gray-600">
                                            <span className="font-semibold">Opens:</span> {new Date(quiz.openTime).toLocaleString()}
                                          </p>
                                        )}
                                        {quiz.closeTime && (
                                          <p className="text-gray-600">
                                            <span className="font-semibold">Closes:</span> {new Date(quiz.closeTime).toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {/* Only show Take Quiz button for students */}
                                  {user?.role === 'student' && (
                                    <div className="md:ml-6 w-full md:w-auto">
                                      <button
                                        onClick={() => handleTakeQuiz(quiz.id)}
                                        disabled={isDisabled}
                                        className={`w-full lg:w-auto px-6 lg:px-8 py-3 lg:py-4 rounded-xl font-bold text-base lg:text-lg transition-all duration-300 shadow-lg ${
                                          isDisabled
                                            ? 'bg-gray-300 text-gray-600 cursor-not-allowed shadow-none'
                                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-2xl transform hover:-translate-y-1'
                                        }`}
                                        title={
                                          isExhausted ? 'You have used all attempts for this quiz' :
                                          isClosed ? 'This quiz is closed' : 
                                          isUpcoming ? 'This quiz is not yet available' : 
                                          'Take this quiz'
                                        }
                                      >
                                        {isExhausted ? '❌ No Attempts' : isClosed ? '🔒 Closed' : isUpcoming ? '⏰ Not Available' : '▶️ Take Quiz'}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Transcription Modal */}
      {transcriptionModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Only close if clicking the backdrop itself, not its children
            if (e.target === e.currentTarget) {
              closeTranscriptionModal();
            }
          }}
          role="presentation"
        >
          <div 
            className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <DocumentIcon className="h-6 w-6" />
                Audio Transcription
              </h3>
              <button
                onClick={closeTranscriptionModal}
                className="text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-20 transition-all rounded-full p-2"
                aria-label="Close transcription modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {transcriptionModal.loading && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Transcribing audio, please wait...</p>
                  <p className="text-sm text-gray-500 mt-2">This may take a minute</p>
                </div>
              )}

              {transcriptionModal.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-red-800">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">{transcriptionModal.error}</span>
                  </div>
                </div>
              )}

              {transcriptionModal.text && !transcriptionModal.loading && (
                <div className="space-y-4">
                  {/* Custom Audio Player for Sync */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
                    <p className="text-sm text-blue-800 mb-3 font-medium flex items-center gap-2">
                      <MusicalNoteIcon className="h-5 w-5" />
                      Play audio to see synchronized highlighting
                    </p>
                    
                    {/* Hidden audio element */}
                    <audio
                      key={transcriptionModal.audioUrl}
                      ref={audioRef}
                      src={transcriptionModal.audioUrl}
                      preload="metadata"
                      crossOrigin="anonymous"
                      onTimeUpdate={handleAudioTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={() => {
                        setCurrentWordIndex(-1);
                        setIsPlaying(false);
                      }}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onError={(e) => {
                        console.error('Audio loading error:', e);
                        setTranscriptionModal(prev => ({
                          ...prev,
                          error: 'Unable to load audio. Please check your connection and try again.'
                        }));
                      }}
                    >
                      {/* Empty track for accessibility - transcription text displayed below serves as caption */}
                      <track kind="captions" />
                    </audio>

                    {/* Custom Controls */}
                    <div className="space-y-3">
                      {/* Play/Pause and Time */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={togglePlayPause}
                          className="flex-shrink-0 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                        >
                          {isPlaying ? (
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          )}
                        </button>

                        {/* Time Display */}
                        <div className="flex-1 flex items-center gap-2">
                          <span className="text-sm font-mono text-gray-700 min-w-[45px]">
                            {formatTime(currentTime)}
                          </span>
                          
                          {/* Progress Bar */}
                          <input
                            type="range"
                            min="0"
                            max={duration || 0}
                            value={currentTime}
                            onChange={handleSeek}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                            style={{
                              background: `linear-gradient(to right, #2563eb 0%, #2563eb ${(currentTime / duration) * 100}%, #e5e7eb ${(currentTime / duration) * 100}%, #e5e7eb 100%)`
                            }}
                          />
                          
                          <span className="text-sm font-mono text-gray-700 min-w-[45px] text-right">
                            {formatTime(duration)}
                          </span>
                        </div>
                      </div>

                      {/* Volume Control */}
                      <div className="flex items-center gap-3 pl-1">
                        <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                        </svg>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={volume}
                          onChange={handleVolumeChange}
                          className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          style={{
                            background: `linear-gradient(to right, #2563eb 0%, #2563eb ${volume * 100}%, #e5e7eb ${volume * 100}%, #e5e7eb 100%)`
                          }}
                        />
                        <span className="text-xs text-gray-600 min-w-[35px]">
                          {Math.round(volume * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Transcription with Word-Level Highlighting */}
                  <div className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto" id="transcription-container">
                    <div className="text-gray-800 leading-relaxed">
                      {transcriptionModal.words.length > 0 ? (
                        // Render words with highlighting
                        transcriptionModal.words.map((wordObj, idx) => (
                          <span
                            key={idx}
                            data-word-index={idx}
                            className={`transition-all duration-200 ${
                              idx === currentWordIndex
                                ? 'bg-yellow-300 text-gray-900 font-semibold px-1 rounded'
                                : 'text-gray-800'
                            }`}
                          >
                            {wordObj.word}{' '}
                          </span>
                        ))
                      ) : (
                        // Fallback to plain text if no word timestamps
                        <p className="whitespace-pre-wrap">{transcriptionModal.text}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(transcriptionModal.text);
                        alert('Transcription copied to clipboard!');
                      }}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy Text
                    </button>
                    <button
                      onClick={() => {
                        const blob = new Blob([transcriptionModal.text], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'transcription.txt';
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CourseViewPage;

